// src\components\VideoEngine\Templates\TikTok_Portrait\Sequences\RankingSlide.js

import React from "react";
import {
    AbsoluteFill,
    Sequence,
    Audio,
    useCurrentFrame,
    interpolate,
    spring,
    useVideoConfig,
    Img,
} from "remotion";
import { SmartBackground } from "../../../Common/SmartBackground";
import { KaraokeCaptions } from "../../../Common/KaraokeCaptions";

// --- SUB-COMPONENT: Rank Badge ---
const RankBadge = ({ rank, theme, delay }) => {
    const frame = useCurrentFrame();
    const { fps } = useVideoConfig();
    const isNumeric = /^\d+$/.test(rank);
    const isRankableNumber = isNumeric && parseInt(rank, 10) <= 50;

    const scale = spring({
        frame: frame - delay,
        fps,
        config: { damping: 12, stiffness: 200 },
    });

    const rotation = interpolate(scale, [0, 1], [-20, 0]);

    return (
        <div
            style={{
                position: "absolute",
                top: "50%", // <--- Anchored to the vertical equator
                left: 60,
                zIndex: 15,
                transform: `translateY(-50%) scale(${scale}) rotate(${rotation}deg)`,
                transformOrigin: "center center",
                background: theme.primary,
                color: "white",
                boxShadow: "0 10px 25px rgba(0,0,0,0.4)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: theme.fontDisplay || theme.fontMain,
                fontWeight: 900,
                ...(isNumeric
                    ? {
                        width: 120, // Slightly scaled up for portrait impact
                        height: 120,
                        borderRadius: "50%",
                        fontSize: 56,
                    }
                    : {
                        width: "auto",
                        height: "auto",
                        minHeight: 80,
                        padding: "20px 40px",
                        borderRadius: "20px",
                        fontSize: 45,
                        whiteSpace: "nowrap",
                    }),
            }}
        >
            <span style={{ marginTop: 2 }}>
                {isRankableNumber ? `#${rank}` : rank.toUpperCase()}
            </span>
        </div>
    );
};

export const RankingSlide = ({
    item,
    theme,
    brand,
    duration,
    ENABLE_AUDIO = true,
}) => {
    const frame = useCurrentFrame();
    const { fps } = useVideoConfig();

    const audioOffset = 0; // 🔒 CRITICAL: Forced zero-frame delay for TikTok engagement. Ignores backend payload delays.

    // SPRINGS
    const blockEnter = spring({
        frame: frame - audioOffset,
        fps,
        config: { damping: 20, stiffness: 100 }
    });

    const metricEnter = spring({
        frame: frame - audioOffset - 10,
        fps,
        config: { damping: 20, stiffness: 100 },
    });

    const nameEnter = spring({
        frame: frame - audioOffset - 15,
        fps,
        config: { damping: 20, stiffness: 100 }
    });

    const hookEnter = spring({
        frame: frame - audioOffset - 20,
        fps,
        config: { damping: 20, stiffness: 100 }
    });

    // 💉 NEW INJECTION: Kinetic "Flex" Physics for the Subject Name
    const namePop = spring({
        frame: frame - audioOffset - 25, // Triggers immediately after name settles
        fps,
        config: { damping: 10, stiffness: 300 }
    });

    // 💉 NEW INJECTION: Kinetic Reawakening Pulse
    const reawakenInterval = 250; // Fires roughly every 3 seconds at 30fps
    const cycleFrame = Math.max(0, (frame - audioOffset - 25)) % reawakenInterval;
    const pulsePop = spring({
        frame: cycleFrame,
        fps,
        config: { damping: 10, stiffness: 300 }
    });

    // ANIMATIONS

    // Slide the editorial block up from the bottom
    const blockY = interpolate(blockEnter, [0, 1], [100, 0]);

    // Push the image up slightly to reveal the block
    const subjectPush = interpolate(blockEnter, [0, 1], [0, -10]);

    // 💉 NEW INJECTION: Subtle but sharp scale manipulation
    //const nameScale = interpolate(namePop, [0, 0.5, 1], [1, 1.05, 1]);
    //const nameScale = interpolate(namePop, [0, 0.5, 1], [1, 1.05, 1]);

    // 💉 NEW INJECTION: Combine the initial entrance pop with the recurring pulse
    const initialScale = interpolate(namePop, [0, 0.5, 1], [1, 1.05, 1]);
    const pulseScale = interpolate(pulsePop, [0, 0.5, 1], [1, 1.05, 1]);
    const nameScale = initialScale * pulseScale;

    // 💉 NEW INJECTION: Dynamically shrink from Full Bleed down to your precise target
    const visualHeight = interpolate(blockEnter, [0, 1], [100, 57]);

    // 💉 NEW INJECTION: Aggressive Ken Burns scaling tied to master duration
    const aggressiveScale = interpolate(frame, [0, duration], [1.75, 1]);

    // 💉 ACCELERATED SHEEN LOOP
    // We decouple the sheen from the master slide duration.
    // By using modulo (%), we force the frame counter to reset every 75 frames (2.5 seconds).
    // This creates a consistent, repeating sweep across the background.
    const sheenCycleLength = 300;
    const loopedFrame = frame % sheenCycleLength;
    const sheenPos = interpolate(loopedFrame, [0, sheenCycleLength], [0, 200]);

    const watermarkOpacity = interpolate(
        frame - audioOffset,
        [0, 30],
        [0, 0.6],
        { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
    );

    return (
        <AbsoluteFill style={{ backgroundColor: "#000", overflow: "hidden" }}>
            {ENABLE_AUDIO && item.props?.audioUrl && (
                <Sequence from={audioOffset}>
                    <Audio src={item.props.audioUrl} />
                </Sequence>
            )}

            {/* 1. VISUAL LAYER */}
            <AbsoluteFill style={{
                height: `${visualHeight}%`, // 💉 Binds the physical height to the rising curtain
                // 🗑️ ACTION: Delete the `transform: translateY(...)` line completely
                backgroundColor: "#111",
                transform: `scale(${aggressiveScale})`, // 💉 Applied Ken Burns
                transformOrigin: "center center"
            }}>
                <SmartBackground src={item.image || ""} duration={duration} />
            </AbsoluteFill>

            {/* 2. EDITORIAL BLOCK (Bottom 50%) */}
            <AbsoluteFill style={{
                top: "50%",
                height: "50%",
                width: "100%",
                backgroundColor: theme.accent,
                zIndex: 2,
                boxShadow: "0 -10px 50px rgba(0,0,0,0.5)",
                transform: `translateY(${blockY}%)`
            }}>
                {/* Texture */}
                <AbsoluteFill style={{
                    background: `linear-gradient(180deg, ${theme.primary}11 0%, transparent 100%)`
                }} />

                {/* 💉 NEW INJECTION: Moving Gloss/Sheen */}
                <AbsoluteFill style={{
                    background: `linear-gradient(115deg, transparent 40%, rgba(255,255,255,0.1) 50%, transparent 60%)`,
                    backgroundSize: "200% 100%",
                    backgroundPosition: `${sheenPos}% 0`,
                }} />
            </AbsoluteFill>

            {/* 3. CONTENT LAYER */}
            <AbsoluteFill style={{ zIndex: 3 }}>

                {/* A. RANK BADGE (Anchored to equator) */}
                {item.rank && (
                    <RankBadge rank={item.rank} theme={theme} delay={audioOffset + 5} />
                )}

                {/* B. TEXT CONTAINER (Constrained to bottom 50%) */}
                <div style={{
                    position: 'absolute',
                    top: "50%",
                    width: "100%",
                    height: '50%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    padding: '80px 140px 220px 60px', // Top padding clears the badge
                }}>

                    {/* METRIC */}
                    {item.metric && (
                        <div
                            style={{
                                transform: `translateY(${(1 - metricEnter) * 30}px)`,
                                opacity: metricEnter,
                                marginBottom: 0,
                                flexShrink: 0,
                                willChange: "transform, opacity",
                            }}
                        >
                            <h2
                                style={{
                                    fontSize: 70, // Scaled for mobile
                                    lineHeight: 1.2,
                                    fontFamily: theme.fontDisplay || theme.fontMain,
                                    fontWeight: 400,
                                    color: "rgba(255,255,255,0.8)",
                                    margin: 0,
                                    textShadow: `2px 2px 0px ${theme.primary}`,
                                }}
                            >
                                {item.metric.toUpperCase()}
                            </h2>
                        </div>
                    )}

                    {/* NAME */}
                    <div
                        style={{
                            transform: `translateX(${(1 - nameEnter) * -30}px)`,
                            opacity: nameEnter,
                            flexShrink: 0,
                            willChange: "transform, opacity",
                        }}
                    >
                        <h1
                            style={{
                                fontSize: 100, // Scaled for mobile width
                                fontFamily: theme.fontMain,
                                fontWeight: 700,
                                color: "rgba(255,255,255,0.9)",
                                textTransform: "uppercase",
                                letterSpacing: 1,
                                margin: "0 0 20px 0",
                                borderLeft: `6px solid ${theme.secondary}`,
                                paddingLeft: 24,
                                lineHeight: 1.1,
                            }}
                        >
                            <span style={{
                                display: "inline-block",
                                transform: `scale(${nameScale})`,
                                borderBottom: namePop > 0.1 ? `4px solid ${theme.secondary}` : "4px solid transparent",
                                transformOrigin: "left center"
                            }}>
                                {item.name}
                            </span>
                        </h1>
                    </div>

                    {/* KARAOKE */}
                    {(item.hook || (item.captions && item.captions.length > 0)) && (
                        <div
                            style={{
                                transform: `translateY(${(1 - hookEnter) * 30}px)`,
                                opacity: hookEnter,
                                marginTop: 10,
                                display: "flex",
                                alignItems: "flex-start", // 💉 Changed from flex-start to center vertically
                                justifyContent: "flex-start", // 💉 Added to center horizontally
                                width: "100%", // 🔒 CRITICAL: Breaks out of the parent's shrink-wrap
                                flexGrow: 1, // 💉 CRITICAL: Replaces `height: 220`. Consumes all remaining vertical space.
                                flexShrink: 0,
                                willChange: "transform, opacity",
                            }}
                        >
                            {item.captions && item.captions.length > 0 ? (
                                <div style={{ fontSize: 65, fontFamily: theme.fontMain, width: "100%", textAlign: "center" }}>
                                    <KaraokeCaptions
                                        captions={item.captions}
                                        theme={theme}
                                        delay={audioOffset}
                                        align="flex-start"
                                    />
                                </div>
                            ) : (
                                <p style={{ fontSize: 32, fontFamily: theme.fontMain, color: "#fff", margin: 0, lineHeight: 1.3 }}>
                                    {item.hook}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </AbsoluteFill>

            {/* BRAND LOCKUP (Safe Zone - Bottom Left to avoid TikTok UI) */}
            <div
                style={{
                    position: "absolute",
                    bottom: 120,
                    left: 60,
                    zIndex: 2,
                    opacity: watermarkOpacity,
                    willChange: "opacity",
                }}
            >
                <h3
                    style={{
                        color: "white",
                        fontFamily: theme.fontMain,
                        fontWeight: 700,
                        letterSpacing: 4,
                        margin: 0,
                        fontSize: 50,
                        textShadow: "0px 4px 15px rgba(0,0,0,0.9)",
                    }}
                >
                    LOVE{brand ? brand.toUpperCase() : "INC"}
                </h3>
            </div>

            {/* COMPLIANCE WATERMARK */}
            {item.slide_image_citation_string && (
                <div
                    style={{
                        position: "absolute",
                        bottom: 100,
                        left: 60,
                        zIndex: 11,
                        color: "rgba(255, 255, 255, 0.4)",
                        fontSize: 15,
                        fontFamily: theme.fontMain,
                        textTransform: "uppercase",
                        letterSpacing: 1,
                        textShadow: "1px 1px 4px rgba(0,0,0,0.9)",
                    }}
                >
                    {item.slide_image_citation_string}
                </div>
            )}

            {/* PROGRESS BAR */}
            <div
                style={{
                    position: "absolute", bottom: 0, left: 0, height: 12, width: "100%",
                    background: "rgba(255,255,255,0.2)", zIndex: 10
                }}
            >
                <div
                    style={{
                        height: "100%",
                        width: `${(frame / duration) * 100}%`,
                        background: theme.primary,
                        boxShadow: `0 0 20px ${theme.primary}`,
                        position: "relative"
                    }}
                >
                    <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 4, background: "white", boxShadow: "0 0 15px white" }} />
                </div>
            </div>
        </AbsoluteFill>
    );
};